import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { previewPattern, hasCounterToken, estimateMaxLength, MAX_NUMBER_LENGTH } from '../patternPreview'
import { useCreateScheme, useUpdateScheme } from '../hooks'
import type { NumberingScheme, ResetCycle } from '../types'
import { RESET_CYCLE_LABELS } from '../types'

const CODE_REGEX = /^[a-z0-9-]+$/

const RESET_CYCLE_OPTIONS: { value: ResetCycle; label: string }[] = [
  { value: 'YEARLY', label: RESET_CYCLE_LABELS.YEARLY },
  { value: 'MONTHLY', label: RESET_CYCLE_LABELS.MONTHLY },
  { value: 'NEVER', label: RESET_CYCLE_LABELS.NEVER },
]

type Props = {
  editing: NumberingScheme | null
  onClose: () => void
}

export function SchemeFormDialog({ editing, onClose }: Props) {
  const create = useCreateScheme()
  const update = useUpdateScheme()

  const [code, setCode] = useState(editing?.code ?? '')
  const [label, setLabel] = useState(editing?.label ?? '')
  const [pattern, setPattern] = useState(editing?.pattern ?? '')
  const [counterStart, setCounterStart] = useState(editing?.counterStart ?? 1)
  const [resetCycle, setResetCycle] = useState<ResetCycle>(editing?.resetCycle ?? 'NEVER')
  const [isActive, setIsActive] = useState(editing?.isActive ?? true)
  const [error, setError] = useState<string | null>(null)

  const preview = pattern && hasCounterToken(pattern) ? previewPattern(pattern, counterStart) : null
  const estimatedLength = estimateMaxLength(pattern)
  const tooLong = estimatedLength > MAX_NUMBER_LENGTH
  const counterColor = tooLong
    ? 'text-red-600'
    : estimatedLength >= MAX_NUMBER_LENGTH - 2 ? 'text-amber-600' : 'text-slate-400'

  const submit = () => {
    setError(null)
    if (!editing && !CODE_REGEX.test(code)) {
      setError('Kod: tylko małe litery, cyfry i myślniki.')
      return
    }
    if (!label.trim()) { setError('Etykieta nie może być pusta.'); return }
    if (!pattern.trim()) { setError('Wzorzec nie może być pusty.'); return }
    if (!hasCounterToken(pattern)) {
      setError('Wzorzec musi zawierać token licznika: {N}, {####} lub {HEX8}.')
      return
    }
    if (tooLong) {
      setError(`Wzorzec może wygenerować numer do ${estimatedLength} znaków, limit to ${MAX_NUMBER_LENGTH}.`)
      return
    }
    if (counterStart < 0) { setError('Start licznika nie może być ujemny.'); return }

    if (editing) {
      update.mutate(
        { code: editing.code, payload: { label, pattern, counterStart, resetCycle, isActive } },
        { onSuccess: onClose, onError: () => setError('Błąd zapisu.') },
      )
    } else {
      create.mutate(
        { code, label, pattern, counterStart, resetCycle },
        { onSuccess: onClose, onError: () => setError('Błąd tworzenia schematu.') },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[520px] shadow-xl max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="text-base font-semibold">
            {editing ? `Edycja schematu: ${editing.code}` : 'Nowy schemat numeracji'}
          </Dialog.Title>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value.toLowerCase())}
                disabled={!!editing || isSubmitting}
                className="border rounded px-2 py-1 font-mono disabled:bg-slate-50"
                placeholder="np. umowa-pv"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Etykieta</span>
              <input
                value={label}
                onChange={e => setLabel(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1"
                placeholder="np. Umowa PV - schemat roczny"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Wzorzec</span>
              <input
                value={pattern}
                onChange={e => setPattern(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="np. UMW/{YYYY}/{####}"
              />
              <span className="text-[10px] text-slate-400">
                Tokeny: {'{YYYY}'} {'{YY}'} {'{MM}'} {'{N}'} {'{####}'} {'{HEX8}'} {'{INITIALS}'} - inicjały handlowca (np. JK)
              </span>
              {pattern && (
                <span className={`text-[10px] ${counterColor}`}>
                  Szacowana maks. długość: {estimatedLength} / {MAX_NUMBER_LENGTH} znaków
                  {tooLong && ' - przekroczono limit'}
                </span>
              )}
              {preview && (
                <span className="text-xs text-sky-700 font-mono bg-sky-50 rounded px-2 py-1">
                  Podgląd: <strong>{preview}</strong>
                </span>
              )}
              {pattern && !hasCounterToken(pattern) && (
                <span className="text-xs text-amber-600">
                  Brak tokenu licznika - wzorzec jest nieprawidłowy.
                </span>
              )}
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Start licznika</span>
              <input
                type="number"
                min={0}
                value={counterStart}
                onChange={e => setCounterStart(Number(e.target.value))}
                disabled={isSubmitting}
                className="border rounded px-2 py-1 w-28"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Cykl resetowania</span>
              <Combobox<ResetCycle>
                value={resetCycle}
                onChange={v => v && setResetCycle(v)}
                options={RESET_CYCLE_OPTIONS}
                disabled={isSubmitting}
              />
            </label>

            {editing && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4"
                />
                <span>Schemat aktywny</span>
              </label>
            )}

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>Anuluj</button>
              <button
                type="button"
                onClick={submit}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
